/**
 * Default code and dependencies configuration
 *
 * The DEFAULT_CODE and DEFAULT_DEPENDENCIES define the minimal-base project structure.
 * Entry point: App.js using React Navigation (createStaticNavigation)
 * This works with tile-run-engine's custom bundler.
 *
 * Single router: React Navigation only (no Expo Router).
 */

import { TilePacketDependencies, TilePacketFiles } from '@apptile/tile-packet-sdk';

export const DEFAULT_METADATA_NAME = 'TilePacket - React Native in the browser';
export const DEFAULT_METADATA_DESCRIPTION_EMPTY = `Write code in Expo's online editor and instantly use it on your phone.`;
export const DEFAULT_METADATA_DESCRIPTION_SAVED = `Try this project on your phone! Use Expo's online editor to make changes and save your own copy.`;

export const DEFAULT_DESCRIPTION = 'No description';

export const DEFAULT_CODE: TilePacketFiles = {
  'App.js': {
    contents: `import React from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ThemeProvider, I18nProvider, AnalyticsProvider, ErrorBoundaryProvider } from '@/core/providers';
import Navigation from './src/navigation';

export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <ErrorBoundaryProvider>
          <ThemeProvider>
            <I18nProvider>
              <AnalyticsProvider>
                <Navigation />
              </AnalyticsProvider>
            </I18nProvider>
          </ThemeProvider>
        </ErrorBoundaryProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}
`,
    type: 'CODE',
  },
  'app.json': {
    contents: `{
  "expo": {
    "name": "Minimal Base",
    "slug": "minimal-base",
    "version": "1.0.0",
    "orientation": "portrait",
    "icon": "./assets/icon.png",
    "scheme": "minimal-base",
    "userInterfaceStyle": "automatic",
    "newArchEnabled": true,
    "splash": {
      "image": "./assets/splash-icon.png",
      "resizeMode": "contain",
      "backgroundColor": "#ffffff"
    },
    "ios": {
      "supportsTablet": true,
      "bundleIdentifier": "com.example.minimalbase"
    },
    "android": {
      "adaptiveIcon": {
        "foregroundImage": "./assets/adaptive-icon.png",
        "backgroundColor": "#ffffff"
      },
      "package": "com.example.minimalbase"
    },
    "web": {
      "bundler": "metro",
      "output": "static",
      "favicon": "./assets/favicon.png"
    },
    "plugins": [
      "expo-font",
      "expo-secure-store"
    ]
  }
}
`,
    type: 'CODE',
  },
  'babel.config.js': {
    contents: `module.exports = function (api) {
  api.cache(true);
  return {
    presets: ["babel-preset-expo"],
    plugins: [
      [
        "module-resolver",
        {
          root: ["./"],
          alias: {
            "@": "./src",
          },
        },
      ],
      "react-native-reanimated/plugin",
    ],
  };
};
`,
    type: 'CODE',
  },
  'metro.config.js': {
    contents: `const { getDefaultConfig } = require("expo/metro-config");

const config = getDefaultConfig(__dirname);

module.exports = config;
`,
    type: 'CODE',
  },
  'package.json': {
    contents: `{
  "name": "minimal-base",
  "version": "1.0.0",
  "main": "node_modules/expo/AppEntry.js",
  "private": true,
  "scripts": {
    "start": "expo start",
    "android": "expo start --android",
    "ios": "expo start --ios",
    "web": "expo start --web",
    "lint": "expo lint",
    "test": "jest --watchAll"
  },
  "jest": {
    "preset": "jest-expo"
  },
  "dependencies": {
    "@expo/metro-runtime": "~6.1.1",
    "@expo/vector-icons": "^15.0.3",
    "@react-native-async-storage/async-storage": "2.2.0",
    "expo": "~54.0.25",
    "expo-constants": "~18.0.10",
    "expo-font": "~14.0.7",
    "expo-linking": "~8.0.9",
    "expo-secure-store": "~15.0.7",
    "expo-splash-screen": "~31.0.10",
    "expo-status-bar": "~3.0.8",
    "expo-system-ui": "~6.0.8",
    "react": "19.1.0",
    "react-dom": "19.1.0",
    "react-native": "0.81.5",
    "react-native-gesture-handler": "~2.28.0",
    "react-native-reanimated": "~4.1.1",
    "react-native-safe-area-context": "~5.6.0",
    "react-native-screens": "~4.16.0",
    "react-native-web": "~0.21.0",
    "react-native-worklets": "^0.5.1",
    "react-native-worklets-core": "^1.6.2"
  },
  "devDependencies": {
    "@types/react": "~19.1.10",
    "babel-plugin-module-resolver": "^5.0.2",
    "eslint": "^9.25.0",
    "eslint-config-expo": "~10.0.0",
    "jest": "^29.2.1",
    "jest-expo": "~54.0.10",
    "typescript": "~5.9.2"
  }
}
`,
    type: 'CODE',
  },
  'src/components/index.ts': {
    contents: `export * from "./ui";
export * from "./modules";
`,
    type: 'CODE',
  },
  'src/components/modules/index.ts': {
    contents: `export {masni1} from './mansi1';
`,
    type: 'CODE',
  },
  'src/components/modules/mansi1.ts': {
    contents: `// mansi1 module placeholder
export const masni1 = {};
`,
    type: 'CODE',
  },
  'src/components/ui/Button.tsx': {
    contents: `import React from "react";
import {
  Pressable,
  Text,
  ActivityIndicator,
  PressableProps,
  StyleSheet,
  ViewStyle,
  TextStyle,
} from "react-native";
import { useTheme } from "@/core/providers";

/**
 * Button Component
 *
 * Native StyleSheet button with variants. Colors come from the active
 * theme via \`getThemeValue\` so light/dark switches in ThemeProvider
 * automatically restyle every Button.
 */

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends PressableProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  children: React.ReactNode;
}

const sizeStyles: Record<ButtonSize, { container: ViewStyle; text: TextStyle }> = {
  sm: { container: { paddingHorizontal: 12, paddingVertical: 8 }, text: { fontSize: 14 } },
  md: { container: { paddingHorizontal: 16, paddingVertical: 12 }, text: { fontSize: 16 } },
  lg: { container: { paddingHorizontal: 24, paddingVertical: 16 }, text: { fontSize: 18 } },
};

/** Map a variant to \`{ container, text }\` style fragments using the
 *  current theme's resolved color values. */
function getVariantStyles(
  variant: ButtonVariant,
  getThemeValue: (path: string) => any,
): { container: ViewStyle; text: TextStyle } {
  switch (variant) {
    case "primary":
      return {
        container: { backgroundColor: getThemeValue("colors.primary") },
        text: { color: getThemeValue("colors.onPrimary") },
      };
    case "secondary":
      return {
        container: { backgroundColor: getThemeValue("colors.secondary") },
        text: { color: getThemeValue("colors.onPrimary") },
      };
    case "outline":
      return {
        container: {
          backgroundColor: "transparent",
          borderWidth: 1,
          borderColor: getThemeValue("colors.border"),
        },
        text: { color: getThemeValue("colors.text") },
      };
    case "ghost":
      return {
        container: { backgroundColor: "transparent" },
        text: { color: getThemeValue("colors.text") },
      };
  }
}

export function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  disabled,
  children,
  style,
  ...props
}: ButtonProps) {
  const { getThemeValue } = useTheme();
  const variantStyle = getVariantStyles(variant, getThemeValue);
  const sizeStyle = sizeStyles[size];

  const buttonStyle = [
    styles.base,
    variantStyle.container,
    sizeStyle.container,
    (disabled || isLoading) && styles.disabled,
  ];

  return (
    <Pressable
      style={(state) => [
        ...buttonStyle,
        typeof style === "function" ? style(state) : style,
      ]}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <ActivityIndicator size="small" color={variantStyle.text.color as string} />
      ) : typeof children === "string" ? (
        <Text style={[styles.text, variantStyle.text, sizeStyle.text]}>
          {children}
        </Text>
      ) : (
        children
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
  },
  text: {
    fontWeight: "500",
  },
  disabled: {
    opacity: 0.5,
  },
});
`,
    type: 'CODE',
  },
  'src/components/ui/Input.tsx': {
    contents: `import { forwardRef } from "react";
import { TextInput, TextInputProps, View, Text, StyleSheet, ViewStyle } from "react-native";
import { useTheme } from "@/core/providers";

/**
 * Input Component
 *
 * Text field with optional label + error message. All colors come from
 * THEME via getThemeValue, so the field restyles when the user toggles
 * light/dark.
 */

interface InputProps extends TextInputProps {
  label?: string;
  error?: string;
  containerStyle?: ViewStyle;
}

export const Input = forwardRef<TextInput, InputProps>(
  ({ label, error, containerStyle, style, ...props }, ref) => {
    const { getThemeValue } = useTheme();

    const inputColors = {
      label: getThemeValue("colors.text"),
      text: getThemeValue("colors.text"),
      placeholder: getThemeValue("colors.muted"),
      background: getThemeValue("colors.background"),
      border: getThemeValue("colors.border"),
      errorBorder: getThemeValue("colors.destructive"),
      error: getThemeValue("colors.destructive"),
    };

    return (
      <View style={[staticStyles.container, containerStyle]}>
        {label && (
          <Text style={[staticStyles.label, { color: inputColors.label }]}>
            {label}
          </Text>
        )}
        <TextInput
          ref={ref}
          style={[
            staticStyles.input,
            {
              backgroundColor: inputColors.background,
              borderColor: error ? inputColors.errorBorder : inputColors.border,
              color: inputColors.text,
            },
            style,
          ]}
          placeholderTextColor={inputColors.placeholder}
          {...props}
        />
        {error && (
          <Text style={[staticStyles.error, { color: inputColors.error }]}>
            {error}
          </Text>
        )}
      </View>
    );
  }
);

Input.displayName = "Input";

/** Layout-only — no colors here; all colors come from theme above. */
const staticStyles = StyleSheet.create({
  container: {
    width: "100%",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 8,
  },
  input: {
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 16,
  },
  error: {
    fontSize: 14,
    marginTop: 4,
  },
});
`,
    type: 'CODE',
  },
  'src/components/ui/Text.tsx': {
    contents: `import { Text as RNText, TextProps as RNTextProps, TextStyle } from "react-native";
import { useTheme } from "@/core/providers";

/**
 * Text Component
 *
 * Variant size + weight come from the THEME's typography presets;
 * color comes from the THEME's color tokens. Editing values in
 * ThemeProvider/themeConstants.tsx propagates everywhere automatically.
 *
 * Variant → typography preset:
 *   h1, h2     → typography.heading
 *   h3         → typography.subheading
 *   body, label → typography.body
 *   caption    → typography.caption
 */

type TextVariant = "h1" | "h2" | "h3" | "body" | "caption" | "label";

interface TextProps extends RNTextProps {
  variant?: TextVariant;
}

/** Map UI variant → THEME paths + per-variant overrides. */
function getVariantConfig(variant: TextVariant): {
  presetPath: string;
  colorPath: string;
  override?: Partial<TextStyle>;
} {
  switch (variant) {
    case "h1":
      return { presetPath: "typography.heading", colorPath: "colors.text", override: { fontSize: 30 } };
    case "h2":
      return { presetPath: "typography.heading", colorPath: "colors.text" };
    case "h3":
      return { presetPath: "typography.subheading", colorPath: "colors.text", override: { fontSize: 20 } };
    case "body":
      return { presetPath: "typography.body", colorPath: "colors.text", override: { fontSize: 16 } };
    case "caption":
      return { presetPath: "typography.caption", colorPath: "colors.muted" };
    case "label":
      return { presetPath: "typography.body", colorPath: "colors.text", override: { fontWeight: "500" } };
  }
}

export function Text({ variant = "body", style, children, ...props }: TextProps) {
  const { getThemeValue } = useTheme();
  const { presetPath, colorPath, override } = getVariantConfig(variant);
  const preset = getThemeValue(presetPath) as {
    fontFamily: string;
    fontSize: number;
    fontWeight: string;
    lineHeight: number;
  };

  const variantStyle: TextStyle = {
    fontFamily: preset.fontFamily,
    fontSize: preset.fontSize,
    fontWeight: preset.fontWeight as TextStyle["fontWeight"],
    // RN expects lineHeight in absolute px, not a multiplier.
    lineHeight: preset.fontSize * preset.lineHeight,
    color: getThemeValue(colorPath),
    ...override,
  };

  return (
    <RNText style={[variantStyle, style]} {...props}>
      {children}
    </RNText>
  );
}
`,
    type: 'CODE',
  },
  'src/components/ui/index.ts': {
    contents: `export { Text } from "./Text";
export { Button } from "./Button";
export { Input } from "./Input";
`,
    type: 'CODE',
  },
  'src/config/constants.ts': {
    contents: `// App configuration constants

// App info
export const APP_NAME = "Minimal Base";
export const APP_VERSION = "1.0.0";

// API configuration
export const API_URL = process.env.EXPO_PUBLIC_API_URL || "http://localhost:3000";

// AsyncStorage keys
export const STORAGE_KEYS = {
  THEME: "user_theme",
  LANGUAGE: "user_language",
  ONBOARDING_COMPLETE: "onboarding_complete",
} as const;

// Feature flags
export const FEATURES = {
  ENABLE_ANALYTICS: true,
  ENABLE_DARK_MODE: true,
} as const;

// Supported languages
export const SUPPORTED_LOCALES = {
  en: "English",
  es: "Español",
} as const;

export type Locale = keyof typeof SUPPORTED_LOCALES;
export const DEFAULT_LOCALE: Locale = "en";

// Timeouts
export const API_TIMEOUT = 30000; // 30 seconds
export const DEBOUNCE_DELAY = 300; // 300ms

// Error messages
export const ERROR_MESSAGES = {
  NETWORK: "Network error. Please check your connection.",
  UNAUTHORIZED: "You are not authorized.",
  NOT_FOUND: "Resource not found.",
  SERVER: "Server error. Please try again.",
  VALIDATION: "Please check your input.",
} as const;
`,
    type: 'CODE',
  },
  'src/config/index.ts': {
    contents: `export * from "./constants";
`,
    type: 'CODE',
  },
  'src/config/integrations.ts': {
    contents: `// Integration configuration - replace with your actual keys
export const shopify = {
    apiKey: "your-api-key",
    apiSecret: "your-api-secret",
};
`,
    type: 'CODE',
  },
  'src/core/i18n/index.ts': {
    contents: `export { translations, type Locale, type TranslationKey } from "./translations";
`,
    type: 'CODE',
  },
  'src/core/i18n/translations.ts': {
    contents: `// Translation strings - use {{param}} for dynamic values
const en = {
  "common.loading": "Loading...",
  "common.error": "An error occurred",
  "common.retry": "Retry",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.done": "Done",
  "common.next": "Next",
  "common.back": "Back",
  "common.search": "Search",
  "auth.signIn": "Sign In",
  "auth.signUp": "Sign Up",
  "auth.signOut": "Sign Out",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.forgotPassword": "Forgot Password?",
  "auth.createAccount": "Create Account",
  "auth.welcomeBack": "Welcome back!",
  "settings.title": "Settings",
  "settings.theme": "Theme",
  "settings.language": "Language",
  "settings.notifications": "Notifications",
  "settings.about": "About",
  "settings.version": "Version {{version}}",
  "error.network": "Network error. Please check your connection.",
  "error.generic": "Something went wrong. Please try again.",
  "greeting": "Hello, {{name}}!",
};

const es: typeof en = {
  "common.loading": "Cargando...",
  "common.error": "Ocurrió un error",
  "common.retry": "Reintentar",
  "common.cancel": "Cancelar",
  "common.save": "Guardar",
  "common.delete": "Eliminar",
  "common.done": "Hecho",
  "common.next": "Siguiente",
  "common.back": "Atr\\u00e1s",
  "common.search": "Buscar",
  "auth.signIn": "Iniciar Sesi\\u00f3n",
  "auth.signUp": "Registrarse",
  "auth.signOut": "Cerrar Sesi\\u00f3n",
  "auth.email": "Correo",
  "auth.password": "Contrase\\u00f1a",
  "auth.forgotPassword": "\\u00bfOlvidaste tu contrase\\u00f1a?",
  "auth.createAccount": "Crear Cuenta",
  "auth.welcomeBack": "¡Bienvenido de vuelta!",
  "settings.title": "Configuración",
  "settings.theme": "Tema",
  "settings.language": "Idioma",
  "settings.notifications": "Notificaciones",
  "settings.about": "Acerca de",
  "settings.version": "Versi\\u00f3n {{version}}",
  "error.network": "Error de red. Verifica tu conexi\\u00f3n.",
  "error.generic": "Algo sali\\u00f3 mal. Intenta de nuevo.",
  "greeting": "\\u00a1Hola, {{name}}!",
};

export const translations = {
  en,
  es,
  fr: en,
  de: en,
} as const;

export type Locale = keyof typeof translations;
export type TranslationKey = keyof typeof en;
`,
    type: 'CODE',
  },
  'src/core/index.ts': {
    contents: `/**
 * Core Module
 *
 * Export all core functionality from a single entry point.
 */

export * from "./interfaces";
export * from "./providers";
export * from "./services";
export * from "./i18n";
`,
    type: 'CODE',
  },
  'src/core/interfaces/analytics.ts': {
    contents: `/**
 * Analytics Interface
 *
 * This file defines the contract for analytics tracking.
 * Implement these interfaces to integrate with any analytics provider:
 * - Mixpanel
 * - Amplitude
 * - Segment
 * - PostHog
 * - Firebase Analytics
 * - Custom solution
 */

// Event properties (any key-value pairs)
export type EventProperties = Record<string, string | number | boolean>;

// User properties for identification
export interface UserProperties {
  id: string;
  email?: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
}

// Analytics service interface
export interface Analytics {
  // Track a custom event
  track: (eventName: string, properties?: EventProperties) => void;

  // Identify a user
  identify: (userId: string, properties?: UserProperties) => void;

  // Track screen/page views
  screen: (screenName: string, properties?: EventProperties) => void;

  // Reset analytics (on logout)
  reset: () => void;
}

// Common event names (optional, for consistency)
export const CommonEvents = {
  SIGN_UP: "sign_up",
  SIGN_IN: "sign_in",
  SIGN_OUT: "sign_out",
  SCREEN_VIEW: "screen_view",
  BUTTON_CLICK: "button_click",
  ERROR: "error",
} as const;
`,
    type: 'CODE',
  },
  'src/core/interfaces/api.ts': {
    contents: `/**
 * API Interface
 *
 * This file defines the contract for API communication.
 * Implement these interfaces to integrate with any backend:
 * - REST API (fetch)
 * - GraphQL (Apollo, URQL)
 * - tRPC
 * - Convex
 * - Supabase
 */

// Standard API response wrapper
export interface ApiResponse<T> {
  data: T | null;
  error: ApiError | null;
  status: number;
}

// Error structure
export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

// HTTP methods
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// Request options
export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string>;
  body?: unknown;
}

// API client interface
export interface ApiClient {
  get: <T>(url: string, options?: RequestOptions) => Promise<ApiResponse<T>>;
  post: <T>(url: string, options?: RequestOptions) => Promise<ApiResponse<T>>;
  put: <T>(url: string, options?: RequestOptions) => Promise<ApiResponse<T>>;
  patch: <T>(url: string, options?: RequestOptions) => Promise<ApiResponse<T>>;
  delete: <T>(url: string, options?: RequestOptions) => Promise<ApiResponse<T>>;

  // Set auth token for authenticated requests
  setAuthToken: (token: string | null) => void;
}

// API configuration
export interface ApiConfig {
  baseUrl: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
}
`,
    type: 'CODE',
  },
  'src/core/interfaces/index.ts': {
    contents: `/**
 * Core Interfaces
 *
 * Export all interfaces from a single entry point.
 * Import like: import { User, Analytics } from "@/core/interfaces";
 */

export * from "./storage";
export * from "./analytics";
export * from "./api";
`,
    type: 'CODE',
  },
  'src/core/interfaces/storage.ts': {
    contents: `import AsyncStorage from "@react-native-async-storage/async-storage";
import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

/**
 * Storage Service
 *
 * Ready-to-use storage implementations.
 * Uses AsyncStorage for general data, SecureStore for sensitive data.
 *
 * Usage:
 *   import { storage, secureStorage } from "@/core/interfaces/storage";
 *   await storage.set("user_preferences", { theme: "dark" });
 *   const prefs = await storage.get("user_preferences");
 */

// ============================================================
// KEY-VALUE STORAGE - For general app data
// ============================================================

export const storage = {
  /**
   * Get a value from storage
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const value = await AsyncStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  },

  /**
   * Save a value to storage
   */
  async set<T>(key: string, value: T): Promise<void> {
    await AsyncStorage.setItem(key, JSON.stringify(value));
  },

  /**
   * Remove a value from storage
   */
  async remove(key: string): Promise<void> {
    await AsyncStorage.removeItem(key);
  },

  /**
   * Clear all storage
   */
  async clear(): Promise<void> {
    await AsyncStorage.clear();
  },
};

// ============================================================
// SECURE STORAGE - For sensitive data (tokens, passwords)
// ============================================================

export const secureStorage = {
  /**
   * Get a secure value (tokens, credentials)
   */
  async get(key: string): Promise<string | null> {
    try {
      // SecureStore only works on native platforms
      if (Platform.OS === "web") {
        return localStorage.getItem(key);
      }
      return await SecureStore.getItemAsync(key);
    } catch {
      return null;
    }
  },

  /**
   * Save a secure value
   */
  async set(key: string, value: string): Promise<void> {
    if (Platform.OS === "web") {
      localStorage.setItem(key, value);
      return;
    }
    await SecureStore.setItemAsync(key, value);
  },

  /**
   * Remove a secure value
   */
  async remove(key: string): Promise<void> {
    if (Platform.OS === "web") {
      localStorage.removeItem(key);
      return;
    }
    await SecureStore.deleteItemAsync(key);
  },
};

// ============================================================
// COLLECTION STORAGE - For arrays of items (like a simple DB)
// ============================================================

export function createCollection<T extends { id: string }>(collectionName: string) {
  const key = \`collection_\${collectionName}\`;

  return {
    /**
     * Get all items in the collection
     */
    async getAll(): Promise<T[]> {
      return (await storage.get<T[]>(key)) || [];
    },

    /**
     * Get a single item by ID
     */
    async getById(id: string): Promise<T | null> {
      const items = await this.getAll();
      return items.find((item) => item.id === id) || null;
    },

    /**
     * Add a new item to the collection
     */
    async create(data: Omit<T, "id">): Promise<T> {
      const items = await this.getAll();
      const newItem = { ...data, id: generateId() } as T;
      items.push(newItem);
      await storage.set(key, items);
      return newItem;
    },

    /**
     * Update an existing item
     */
    async update(id: string, data: Partial<T>): Promise<T | null> {
      const items = await this.getAll();
      const index = items.findIndex((item) => item.id === id);
      if (index === -1) return null;

      items[index] = { ...items[index], ...data };
      await storage.set(key, items);
      return items[index];
    },

    /**
     * Delete an item by ID
     */
    async delete(id: string): Promise<boolean> {
      const items = await this.getAll();
      const filtered = items.filter((item) => item.id !== id);
      if (filtered.length === items.length) return false;

      await storage.set(key, filtered);
      return true;
    },

    /**
     * Clear the entire collection
     */
    async clear(): Promise<void> {
      await storage.remove(key);
    },
  };
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}
`,
    type: 'CODE',
  },
  'src/core/providers/AnalyticsProvider.tsx': {
    contents: `// Analytics provider - provides tracking methods throughout the app
import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { AnalyticsService, analytics as defaultAnalytics } from "../services/analytics";
import { FEATURES } from "@/config";


interface AnalyticsContextType {
  track: (event: string, properties?: Record<string, unknown>) => void;
  screen: (name: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
  service?: AnalyticsService;
}

export function AnalyticsProvider({
  children,
  service = defaultAnalytics,
}: AnalyticsProviderProps) {
  // Initialize analytics on mount
  useEffect(() => {
    if (FEATURES.ENABLE_ANALYTICS) {
      service.initialize();
    }
  }, [service]);

  const track = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      if (!FEATURES.ENABLE_ANALYTICS) return;
      service.track(event, properties);
    },
    [service]
  );

  const screen = useCallback(
    (name: string, properties?: Record<string, unknown>) => {
      if (!FEATURES.ENABLE_ANALYTICS) return;
      service.screen(name, properties);
    },
    [service]
  );

  const identify = useCallback(
    (userId: string, traits?: Record<string, unknown>) => {
      if (!FEATURES.ENABLE_ANALYTICS) return;
      service.identify(userId, traits);
    },
    [service]
  );

  const reset = useCallback(() => {
    service.reset();
  }, [service]);

  return (
    <AnalyticsContext.Provider value={{ track, screen, identify, reset }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
}
`,
    type: 'CODE',
  },
  'src/core/providers/ErrorBoundaryProvider.tsx': {
    contents: `// Error boundary - catches React errors and displays fallback UI
import React, { Component, ReactNode } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundaryProvider extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error.message);
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <View style={styles.container}>
          <Text style={styles.emoji}>😵</Text>
          <Text style={styles.title}>Oops!</Text>
          <Text style={styles.message}>Something went wrong</Text>
          <Text style={styles.errorText}>
            {this.state.error?.message || "Unknown error"}
          </Text>
          <Pressable style={styles.button} onPress={this.handleReset}>
            <Text style={styles.buttonText}>Try Again</Text>
          </Pressable>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    backgroundColor: "#fff",
  },
  emoji: {
    fontSize: 64,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#1a1a1a",
    marginBottom: 8,
  },
  message: {
    fontSize: 18,
    color: "#666",
    marginBottom: 12,
  },
  errorText: {
    fontSize: 14,
    color: "#999",
    textAlign: "center",
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  button: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 12,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
});
`,
    type: 'CODE',
  },
  'src/core/providers/I18nProvider.tsx': {
    contents: `// I18n provider - manages translations and locale with persistence
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { translations, Locale, TranslationKey } from "../i18n";
import { STORAGE_KEYS, DEFAULT_LOCALE } from "@/config";

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  formatNumber: (num: number) => string;
  formatDate: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
  formatCurrency: (amount: number, currency?: string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
  defaultLocale?: Locale;
}

export function I18nProvider({
  children,
  defaultLocale = DEFAULT_LOCALE as Locale,
}: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);
  const [isReady, setIsReady] = useState(false);

  // Load saved locale from storage on mount
  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEYS.LANGUAGE).then((saved) => {
      if (saved && saved in translations) {
        console.log("[I18nProvider] Loaded locale:", saved);
        setLocaleState(saved as Locale);
      }
      setIsReady(true);
    });
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    console.log("[I18nProvider] Setting locale:", newLocale);
    setLocaleState(newLocale);
    AsyncStorage.setItem(STORAGE_KEYS.LANGUAGE, newLocale);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>): string => {
      const translation =
        translations[locale]?.[key] ||
        translations[defaultLocale]?.[key] ||
        key;

      if (!params) return translation;

      return Object.entries(params).reduce(
        (str, [param, value]) =>
          str.replace(new RegExp(\`{{\${param}}}\`, "g"), String(value)),
        translation
      );
    },
    [locale, defaultLocale]
  );

  const formatNumber = useCallback(
    (num: number): string => new Intl.NumberFormat(locale).format(num),
    [locale]
  );

  const formatDate = useCallback(
    (date: Date, options?: Intl.DateTimeFormatOptions): string =>
      new Intl.DateTimeFormat(locale, options).format(date),
    [locale]
  );

  const formatCurrency = useCallback(
    (amount: number, currency = "USD"): string =>
      new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount),
    [locale]
  );

  if (!isReady) return null;

  return (
    <I18nContext.Provider
      value={{ locale, setLocale, t, formatNumber, formatDate, formatCurrency }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
}
`,
    type: 'CODE',
  },
  'src/core/providers/ThemeProvider/index.tsx': {
    contents: `// Theme provider - manages light/dark/system theme with persistence
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useColorScheme } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { STORAGE_KEYS, FEATURES } from "@/config";
import { THEME, Mode } from "./themeConstants";

export type Theme = "light" | "dark" | "system";

/**
 * Walk a dot-separated path into THEME and:
 *   - if the leaf is \`{ light, dark }\` (a color) → return the right hex
 *     for the effective mode.
 *   - otherwise → return the value as-is (typography preset, etc.).
 *
 * Examples:
 *   getThemeValue("colors.primary")           → "#1060E0" / "#5A93F0"
 *   getThemeValue("typography.heading")       → { fontFamily, fontSize, … }
 *   getThemeValue("typography.body.fontSize") → 14
 */
type GetThemeValue = (path: string) => any;

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: Mode;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /** Dot-path lookup into THEME, mode-aware for color leaves. */
  getThemeValue: GetThemeValue;
  /** Raw THEME object (color leaves pre-resolved for the active mode). */
  resolvedTheme: typeof THEME;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
}: ThemeProviderProps) {
  const systemColorScheme = useColorScheme();
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [isReady, setIsReady] = useState(false);

  // Load saved theme from storage on mount
  useEffect(() => {
    if (!FEATURES.ENABLE_DARK_MODE) {
      setIsReady(true);
      return;
    }

    AsyncStorage.getItem(STORAGE_KEYS.THEME).then((saved) => {
      if (saved === "light" || saved === "dark" || saved === "system") {
        console.log("[ThemeProvider] Loaded theme:", saved);
        setThemeState(saved);
      }
      setIsReady(true);
    });
  }, []);

  // Resolve system theme to light/dark
  const effectiveTheme: "light" | "dark" =
    theme === "system" ? systemColorScheme || "light" : theme;

  const setTheme = useCallback(async (newTheme: Theme) => {
    console.log("[ThemeProvider] Setting theme:", newTheme);
    setThemeState(newTheme);
    await AsyncStorage.setItem(STORAGE_KEYS.THEME, newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = effectiveTheme === "light" ? "dark" : "light";
    setTheme(newTheme);
  }, [effectiveTheme, setTheme]);

  // Mode-aware dot-path getter. Memoised on effectiveTheme so consumers
  // re-render when light/dark flips without rebuilding THEME itself.
  const getThemeValue = useCallback<GetThemeValue>(
    (path: string) => {
      const value = path
        .split(".")
        .reduce<any>((node, key) => (node == null ? node : node[key]), THEME);
      return value &&
        typeof value === "object" &&
        "light" in value &&
        "dark" in value
        ? value[effectiveTheme]
        : value;
    },
    [effectiveTheme],
  );

  // THEME with every color leaf collapsed to the active mode's hex —
  // lets callers do \`resolvedTheme.colors.primary\` directly instead of
  // calling \`getThemeValue("colors.primary")\`.
  const resolvedTheme = useMemo(() => {
    const colors: Record<string, string> = {};
    for (const [name, slot] of Object.entries(THEME.colors)) {
      colors[name] = (slot as { light: string; dark: string })[effectiveTheme];
    }
    return { ...THEME, colors } as unknown as typeof THEME;
  }, [effectiveTheme]);

  if (!isReady) return null;

  return (
    <ThemeContext.Provider
      value={{
        theme,
        effectiveTheme,
        setTheme,
        toggleTheme,
        getThemeValue,
        resolvedTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
`,
    type: 'CODE',
  },
  'src/core/providers/ThemeProvider/themeConstants.tsx': {
    contents: `/**
 * themeConstants.tsx
 * ------------------------------------------------------------------
 * One flat object that mirrors what the editor's Theme left panel
 * (tile-web-v2/src/client/components/editor/ThemePanel.tsx) edits.
 * The runtime ThemeProvider reads from this and lets consumers fetch
 * values by dot-path, e.g. \`theme('colors.primary')\`.
 *
 * Every color slot stores both modes; ThemeProvider picks light or
 * dark based on \`effectiveTheme\`. Typography slots aren't dual-mode.
 * ------------------------------------------------------------------
 */

export type Mode = 'light' | 'dark';

/** A color slot — one hex per mode. */
export interface Dual { light: string; dark: string; }

export const THEME = {
  colors: {
    // brand
    primary:     { light: '#1060E0', dark: '#5A93F0' },
    secondary:   { light: '#185490', dark: '#3A8ED0' },
    accent:      { light: '#185409', dark: '#6AB4F0' },
    // surface
    background:  { light: '#FFFFFF', dark: '#131313' },
    surface:     { light: '#F6F6F6', dark: '#1C1C1C' },
    elevated:    { light: '#FCFCFC', dark: '#242424' },
    border:      { light: '#E5E7EB', dark: '#333333' },
    // content
    text:        { light: '#1A1A1A', dark: '#E8E8E8' },
    muted:       { light: '#6B7280', dark: '#AAAAAA' },
    onPrimary:   { light: '#FFFFFF', dark: '#FFFFFF' },
    // status
    success:     { light: '#16A34A', dark: '#4ADE80' },
    warning:     { light: '#D97706', dark: '#FBBF24' },
    destructive: { light: '#DC2626', dark: '#F87171' },
    info:        { light: '#2563EB', dark: '#60A5FA' },
  },
  typography: {
    heading:    { fontFamily: 'System', fontSize: 28, fontWeight: '700', lineHeight: 1.2 },
    subheading: { fontFamily: 'System', fontSize: 20, fontWeight: '600', lineHeight: 1.3 },
    body:       { fontFamily: 'System', fontSize: 16, fontWeight: '400', lineHeight: 1.5 },
    caption:    { fontFamily: 'System', fontSize: 12, fontWeight: '400', lineHeight: 1.4 },
  },
} as const;

export type Theme = typeof THEME;

/** AsyncStorage key the editor writes into when the user saves. */
export const THEME_STORAGE_KEY = '@tile/theme:v1';
`,
    type: 'CODE',
  },
  'src/core/providers/index.ts': {
    contents: `export { AnalyticsProvider, useAnalytics } from "./AnalyticsProvider";
export { I18nProvider, useI18n } from "./I18nProvider";
export { ErrorBoundaryProvider } from "./ErrorBoundaryProvider";
export { ThemeProvider, useTheme, type Theme } from "./ThemeProvider";
`,
    type: 'CODE',
  },
  'src/core/services/analytics.ts': {
    contents: `// Analytics service - extend this class to integrate real analytics providers
type EventProperties = Record<string, unknown>;

export class AnalyticsService {
  protected userId: string | null = null;
  protected isEnabled = true;

  async initialize(): Promise<void> {
    console.log("[Analytics] Initialized (console mode)");
  }

  identify(userId: string, traits?: EventProperties): void {
    this.userId = userId;
    if (this.isEnabled) {
      console.log("[Analytics] Identify:", userId, traits);
    }
  }

  track(event: string, properties?: EventProperties): void {
    if (this.isEnabled) {
      console.log("[Analytics] Track:", event, properties);
    }
  }

  screen(screenName: string, properties?: EventProperties): void {
    if (this.isEnabled) {
      console.log("[Analytics] Screen:", screenName, properties);
    }
  }

  reset(): void {
    this.userId = null;
    console.log("[Analytics] Reset");
  }

  logError(error: Error, context?: EventProperties): void {
    console.error("[Analytics] Error:", error.message, context);
  }

  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }
}

// Default instance uses console logging
export const analytics = new AnalyticsService();
`,
    type: 'CODE',
  },
  'src/core/services/index.ts': {
    contents: `export { AnalyticsService, analytics } from "./analytics";
`,
    type: 'CODE',
  },
  'src/navigation/index.js': {
    contents: `import { createStaticNavigation } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

import HomeScreen from '../screens/Home';
import SettingsScreen from '../screens/Settings';

// Bottom tabs: Home screen
const TabNavigator = createBottomTabNavigator({
  screens: {
    Home: {
      screen: HomeScreen,
      options: {
        title: 'Home',
        headerShown: false,
      },
    },
  },
});

// Root stack: tabs + settings as stack screen
const RootStack = createNativeStackNavigator({
  screens: {
    Tabs: {
      screen: TabNavigator,
      options: {
        headerShown: false,
      },
    },
    Settings: {
      screen: SettingsScreen,
      options: {
        title: 'Settings',
        headerShown: true,
        presentation: 'card',
      },
    },
  },
});

const Navigation = createStaticNavigation(RootStack);
export default Navigation;
`,
    type: 'CODE',
  },
  'src/screens/Home/index.tsx': {
    contents: `// Home screen - demonstrates theme, i18n, and analytics usage
import { useEffect } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useI18n, useAnalytics } from "@/core/providers";
import { APP_NAME, APP_VERSION } from "@/config";

export default function HomeScreen() {
  const navigation = useNavigation();
  const { effectiveTheme, toggleTheme, getThemeValue } = useTheme();
  const { t, locale, formatDate, formatCurrency } = useI18n();
  const { track, screen } = useAnalytics();

  // Track screen view on mount
  useEffect(() => {
    screen("Home");
  }, []);

  const handleTrackEvent = () => {
    track("button_pressed", { button: "demo_button", screen: "home" });
  };

  // Pulled from THEME via getThemeValue so the active mode (light/dark)
  // resolves automatically. Edit values in ThemeProvider/themeConstants.
  const colors = {
    bg: getThemeValue("colors.background"),
    text: getThemeValue("colors.text"),
    muted: getThemeValue("colors.muted"),
    card: getThemeValue("colors.surface"),
    accent: getThemeValue("colors.primary"),
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <View>
            <Text style={[styles.title, { color: colors.text }]}>{APP_NAME}</Text>
            <Text style={[styles.subtitle, { color: colors.muted }]}>
              {t("settings.version", { version: APP_VERSION })}
            </Text>
          </View>
          <Pressable
            style={[styles.settingsButton, { backgroundColor: colors.card }]}
            onPress={() => navigation.navigate("Settings" as never)}
          >
            <Ionicons name="settings-outline" size={24} color={colors.text} />
          </Pressable>
        </View>

        <View style={[styles.card, { backgroundColor: colors.card }]}>
          <Text style={[styles.cardTitle, { color: colors.text }]}>
            🌍 {t("settings.language")}: {locale.toUpperCase()}
          </Text>
          <Text style={[styles.cardText, { color: colors.muted }]}>
            {t("greeting", { name: "Developer" })}
          </Text>
          <Text style={[styles.cardText, { color: colors.muted }]}>
            {formatDate(new Date())} • {formatCurrency(99.99)}
          </Text>
        </View>

        <View style={[styles.card, { backgroundColor: colors.card }]}>
          <Text style={[styles.cardTitle, { color: colors.text }]}>
            🎨 Theme: {effectiveTheme}
          </Text>
          <Pressable
            style={[styles.button, { backgroundColor: colors.accent }]}
            onPress={toggleTheme}
          >
            <Text style={styles.buttonText}>Toggle Theme</Text>
          </Pressable>
        </View>

        <View style={[styles.card, { backgroundColor: colors.card }]}>
          <Text style={[styles.cardTitle, { color: colors.text }]}>
            📊 Analytics
          </Text>
          <Pressable
            style={[styles.button, { backgroundColor: colors.accent }]}
            onPress={handleTrackEvent}
          >
            <Text style={styles.buttonText}>Track Event</Text>
          </Pressable>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
  },
  settingsButton: {
    padding: 10,
    borderRadius: 10,
  },
  card: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
  },
  cardText: {
    fontSize: 14,
    marginBottom: 4,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 8,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
});
`,
    type: 'CODE',
  },
  'src/screens/Settings/index.tsx': {
    contents: `// Settings screen - theme and language preferences
import { useEffect } from "react";
import { View, Text, Pressable, StyleSheet, ScrollView } from "react-native";
import { useTheme, useI18n, useAnalytics, type Theme } from "@/core/providers";
import { type Locale } from "@/core/i18n";
import { SUPPORTED_LOCALES } from "@/config";

export default function SettingsScreen() {
  const { theme, setTheme, getThemeValue } = useTheme();
  const { t, locale, setLocale } = useI18n();
  const { screen, track } = useAnalytics();

  // Track screen view on mount
  useEffect(() => {
    screen("Settings");
  }, []);

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    track("theme_changed", { theme: newTheme });
  };

  const handleLocaleChange = (newLocale: Locale) => {
    setLocale(newLocale);
    track("language_changed", { language: newLocale });
  };

  // Pulled from THEME via getThemeValue so the active mode (light/dark)
  // resolves automatically. Edit values in ThemeProvider/themeConstants.
  const colors = {
    bg: getThemeValue("colors.background"),
    text: getThemeValue("colors.text"),
    muted: getThemeValue("colors.muted"),
    card: getThemeValue("colors.surface"),
    selected: getThemeValue("colors.elevated"),
    accent: getThemeValue("colors.primary"),
  };

  const themeOptions: { value: Theme; label: string; icon: string }[] = [
    { value: "light", label: "Light", icon: "☀️" },
    { value: "dark", label: "Dark", icon: "🌙" },
    { value: "system", label: "System", icon: "⚙️" },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <ScrollView style={styles.content}>
        <Text style={[styles.sectionTitle, { color: colors.muted }]}>
          {t("settings.theme")}
        </Text>
        <View style={[styles.card, { backgroundColor: colors.card }]}>
          {themeOptions.map((option) => (
            <Pressable
              key={option.value}
              style={[
                styles.option,
                theme === option.value && { backgroundColor: colors.selected },
              ]}
              onPress={() => handleThemeChange(option.value)}
            >
              <Text style={[styles.optionIcon]}>{option.icon}</Text>
              <Text style={[styles.optionText, { color: colors.text }]}>
                {option.label}
              </Text>
              {theme === option.value && (
                <Text style={[styles.checkmark, { color: colors.accent }]}>✓</Text>
              )}
            </Pressable>
          ))}
        </View>

        <Text style={[styles.sectionTitle, { color: colors.muted }]}>
          {t("settings.language")}
        </Text>
        <View style={[styles.card, { backgroundColor: colors.card }]}>
          {(Object.entries(SUPPORTED_LOCALES) as [Locale, string][]).map(
            ([code, name]) => (
              <Pressable
                key={code}
                style={[
                  styles.option,
                  locale === code && { backgroundColor: colors.selected },
                ]}
                onPress={() => handleLocaleChange(code)}
              >
                <Text style={[styles.optionText, { color: colors.text }]}>
                  {name}
                </Text>
                {locale === code && (
                  <Text style={[styles.checkmark, { color: colors.accent }]}>✓</Text>
                )}
              </Pressable>
            )
          )}
        </View>

        <Text style={[styles.sectionTitle, { color: colors.muted }]}>
          {t("settings.about")}
        </Text>
        <View style={[styles.card, { backgroundColor: colors.card }]}>
          <Text style={[styles.aboutText, { color: colors.muted }]}>
            Minimal Base Template{"\\n"}
            Built with Expo + React Native{"\\n"}
            Featuring: Theme, I18n, Analytics
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    textTransform: "uppercase",
    marginBottom: 8,
    marginLeft: 4,
  },
  card: {
    borderRadius: 12,
    marginBottom: 24,
    overflow: "hidden",
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  optionIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  optionText: {
    fontSize: 16,
    flex: 1,
  },
  checkmark: {
    fontSize: 16,
    fontWeight: "bold",
  },
  aboutText: {
    padding: 16,
    fontSize: 14,
    lineHeight: 22,
  },
});
`,
    type: 'CODE',
  },
  'src/screens/index.ts': {
    contents: `export { default as HomeScreen } from "./Home";
export { default as SettingsScreen } from "./Settings";
`,
    type: 'CODE',
  },
  'tsconfig.json': {
    contents: `{
  "extends": "expo/tsconfig.base",
  "compilerOptions": {
    "jsx": "react-jsx",
    "strict": true,
    "paths": {
      "@/*": ["./src/*"]
    },
    "baseUrl": "."
  },
  "include": [
    "**/*.ts",
    "**/*.tsx",
    ".expo/types/**/*.ts",
    "expo-env.d.ts",
    "nativewind-env.d.ts"
  ],
  "exclude": ["node_modules"]
}
`,
    type: 'CODE',
  },
};

export const DEFAULT_DEPENDENCIES: TilePacketDependencies = {
  '@expo/metro-runtime': {
    version: '~6.1.1',
  },
  '@expo/vector-icons': {
    version: '^15.0.3',
  },
  '@react-native-async-storage/async-storage': {
    version: '2.2.0',
  },
  expo: {
    version: '~54.0.25',
  },
  'expo-constants': {
    version: '~18.0.10',
  },
  'expo-font': {
    version: '~14.0.7',
  },
  'expo-linking': {
    version: '~8.0.9',
  },
  'expo-secure-store': {
    version: '~15.0.7',
  },
  'expo-splash-screen': {
    version: '~31.0.10',
  },
  'expo-status-bar': {
    version: '~3.0.8',
  },
  'expo-system-ui': {
    version: '~6.0.8',
  },
  'react-native-gesture-handler': {
    version: '~2.28.0',
  },
  'react-native-reanimated': {
    version: '~4.1.1',
  },
  'react-native-safe-area-context': {
    version: '~5.6.0',
  },
  'react-native-screens': {
    version: '~4.16.0',
  },
  'react-native-web': {
    version: '~0.21.0',
  },
  'react-native-worklets': {
    version: '^0.5.1',
  },
  'react-native-worklets-core': {
    version: '^1.6.2',
  },
  '@react-navigation/native': {
    version: '*',
  },
  '@react-navigation/native-stack': {
    version: '*',
  },
  '@react-navigation/bottom-tabs': {
    version: '*',
  },
};
